import React, { useEffect, useRef } from 'react';
import { useCart } from '../context/CartContext';
import { useTutorial } from '../context/TutorialContext';
import { View } from '../types';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight, BadgePercent, Tag } from 'lucide-react';
import { api } from '../services/api';
import { useState } from 'react';

export const CartDrawer: React.FC = () => {
    const { isCartOpen, setIsCartOpen, cart, updateQuantity, removeFromCart, total, itemCount, appliedCoupon, applyCoupon, removeCoupon, discountAmount, finalTotal } = useCart();
    const { setCurrentView } = useTutorial();
    const drawerRef = useRef<HTMLDivElement>(null);
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        setIsValidating(true);
        setCouponError('');

        // Pass subtotal (named total in context originally)
        const res = await api.validateCoupon(couponCode, total);

        if (res.valid && res.coupon) {
            applyCoupon(res.coupon);
            setCouponCode('');
        } else {
            setCouponError(res.message || 'Cupón inválido');
        }
        setIsValidating(false);
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
                setIsCartOpen(false);
            }
        };

        if (isCartOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isCartOpen, setIsCartOpen]);

    if (!isCartOpen) return null;

    const handleCheckout = () => {
        setIsCartOpen(false);
        setCurrentView(View.CHECKOUT);
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity" />

            {/* Drawer */}
            <div
                ref={drawerRef}
                className="relative w-full max-w-md bg-white shadow-2xl h-full flex flex-col animate-in slide-in-from-right duration-300"
            >
                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className="bg-sky-100 p-2 rounded-xl text-sky-600">
                            <ShoppingBag size={20} />
                        </div>
                        <div>
                            <h2 className="font-black text-lg text-slate-800">Tu Carrito</h2>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">{itemCount} items</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsCartOpen(false)}
                        className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Items */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {cart.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center space-y-4 text-slate-400">
                            <ShoppingBag size={48} className="text-slate-200" />
                            <p className="font-medium">Tu carrito está vacío</p>
                            <button
                                onClick={() => setIsCartOpen(false)}
                                className="text-sky-600 font-bold text-sm hover:underline"
                            >
                                Seguir comprando
                            </button>
                        </div>
                    ) : (
                        cart.map(item => (
                            <div key={item.id} className="flex gap-4 p-3 rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-20 h-20 bg-slate-50 rounded-xl overflow-hidden flex-shrink-0">
                                    <img
                                        src={item.image || 'https://via.placeholder.com/150'}
                                        alt={item.name || 'Producto'}
                                        className="w-full h-full object-cover mix-blend-multiply"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150';
                                        }}
                                    />
                                </div>
                                <div className="flex-1 flex flex-col justify-between py-1">
                                    <div>
                                        <h3 className="font-bold text-slate-700 text-sm leading-tight line-clamp-2">{item.name || 'Sin Nombre'}</h3>
                                        <p className="text-xs text-slate-400 font-medium mt-1">{item.category || 'General'}</p>
                                    </div>
                                    <div className="flex items-center justify-between mt-2">
                                        <p className="font-black text-slate-900">${(item.price || 0).toLocaleString()}</p>

                                        <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-1">
                                            <button
                                                onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                                                className="w-6 h-6 flex items-center justify-center bg-white rounded-md shadow-sm text-slate-600 hover:text-red-500 disabled:opacity-50"
                                            >
                                                {item.quantity === 1 ? <Trash2 size={12} /> : <Minus size={12} />}
                                            </button>
                                            <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                                                className="w-6 h-6 flex items-center justify-center bg-white rounded-md shadow-sm text-slate-600 hover:text-green-500"
                                            >
                                                <Plus size={12} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                {cart.length > 0 && (
                    <div className="p-5 border-t border-slate-100 bg-slate-50">
                        {/* Coupon Section */}
                        <div className="mb-4 space-y-2">
                            {appliedCoupon ? (
                                <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-xl flex items-center justify-between animate-in fade-in slide-in-from-bottom-2">
                                    <div className="flex items-center gap-2">
                                        <div className="bg-emerald-100 p-1.5 rounded-lg text-emerald-600">
                                            <Tag size={14} />
                                        </div>
                                        <div>
                                            <p className="text-xs font-black text-emerald-700 uppercase tracking-wider">{appliedCoupon.code}</p>
                                            <p className="text-[10px] font-bold text-emerald-500">Descuento aplicado</p>
                                        </div>
                                    </div>
                                    <button onClick={removeCoupon} className="p-1 hover:bg-emerald-100 rounded-lg text-emerald-400 hover:text-emerald-700 transition-colors">
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <BadgePercent className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                            <input
                                                type="text"
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                                                placeholder="Código de descuento"
                                                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 placeholder:font-medium placeholder:text-slate-400 uppercase"
                                            />
                                        </div>
                                        <button
                                            onClick={handleApplyCoupon}
                                            disabled={!couponCode || isValidating}
                                            className="px-4 py-2 bg-slate-800 text-white rounded-xl font-bold text-xs uppercase tracking-wider disabled:opacity-50 hover:bg-slate-900 transition-all"
                                        >
                                            {isValidating ? '...' : 'Aplicar'}
                                        </button>
                                    </div>
                                    {couponError && <p className="text-[10px] font-bold text-rose-500 mt-1.5 ml-1 animate-in slide-in-from-left-2">{couponError}</p>}
                                </div>
                            )}
                        </div>

                        <div className="space-y-2 mb-4">
                            <div className="flex justify-between text-sm text-slate-500">
                                <span>Subtotal</span>
                                <span>${total.toLocaleString()}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className="flex justify-between text-sm text-emerald-500 font-bold">
                                    <span>Descuento</span>
                                    <span>-${discountAmount.toLocaleString()}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm text-slate-500">
                                <span>Envío</span>
                                <span className="text-green-600 font-medium">Calculado en checkout</span>
                            </div>
                            <div className="flex justify-between text-lg font-black text-slate-800 pt-2 border-t border-slate-200">
                                <span>Total</span>
                                <span>${finalTotal.toLocaleString()}</span>
                            </div>
                        </div>
                        <button
                            onClick={handleCheckout}
                            className="w-full bg-sky-600 text-white py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-sky-700 active:scale-[0.98] transition-all shadow-lg shadow-sky-200"
                        >
                            <span>Proceder al Pago</span>
                            <ArrowRight size={18} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
